// LiveChat — the small pieces both conversation views share. v0.276.0
// ---------------------------------------------------------------------
// Brett, for a Teams meeting with a client (2026-10-30): "put in the favicons
// to the conversation box". Each message carries a round badge: the company's
// logo on the office's messages, the writer's initials on the client's (a
// client has no logo on file). And the conversation says whether it is live,
// in words, every state named (src/utils/live.js):
//   live      new messages show within a few seconds
//   retrying  the check is failing and backing off
//   paused    nobody has touched the page for 15 minutes (a touch resumes)
//   ended     the pass was refused; a reload gets a new one
//   off       live is not set up, or this view does not watch: no badge
//
// v0.278.0 — PresenceNote: the client's page says "<Name> is editing this
// item" (or "is typing a reply") while someone from the office has the same
// item open, with a moving three-dot "thinking" mark.
//
// v0.284.0 — Brett: "I thought we could display the favocon at the top of the
// box if they are on?" PresenceStrip: a row of round badges at the top of the
// conversation, one per person on that item now (the company logo for the
// office, initials for a client or subcontractor), a small dot on whoever is
// typing. Being on the item is said by the strip, so PresenceNote now speaks
// only for typing.

import React, { useState } from 'react';
import { theme } from '../theme';

const LIVE_LABEL = {
  live:     { text: 'Live', color: theme.success, dot: true, title: 'New messages show here within a few seconds.' },
  retrying: { text: 'Live — reconnecting…', color: theme.warn, title: 'The check for new messages is failing; it keeps trying.' },
  paused:   { text: 'Live paused — tap to resume', color: theme.textLight, title: 'Paused after 15 minutes untouched. Any touch, key or mouse move resumes it.' },
  ended:    { text: 'Live ended — reload the page', color: theme.danger, title: 'New messages will not show until the page is reloaded.' },
};

// v0.336.0 - "Phone doesn't have live or paused." The office's watch could
// not start (its pass refused or unreachable, or live not set up here) and the
// badge drew nothing. With `onRetry` (the office card) those states say so,
// with a Try again; without it (the client's page) they stay quiet as before.
const LIVE_OFF = {
  off:    { text: 'Live off', title: 'New messages will not show by themselves. Try again, or reopen the item.' },
  failed: { text: 'Live off', title: 'The live watch could not start (no connection, or the server refused it). Try again.' },
};

export function LiveBadge({ state, onRetry = null }) {
  if (onRetry && LIVE_OFF[state]) {
    const o = LIVE_OFF[state];
    return (
      <span data-live={state} title={o.title} style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        fontSize: 11, fontWeight: 600, color: theme.textLight, whiteSpace: 'nowrap',
      }}>
        {o.text} —
        <button type="button" data-live-retry onClick={onRetry} style={{
          border: 'none', background: 'none', padding: '4px 0', font: 'inherit', fontWeight: 600,
          color: theme.brand, textDecoration: 'underline', cursor: 'pointer',
        }}>Try again</button>
      </span>
    );
  }
  const l = LIVE_LABEL[state];
  if (!l) return null;
  return (
    <span data-live={state} title={l.title} style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      fontSize: 11, fontWeight: 600, color: l.color, whiteSpace: 'nowrap',
    }}>
      {l.dot && <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: '50%', background: l.color }} />}
      {l.text}
    </span>
  );
}

// "Sam Client" → "SC"; "client@example.test" → "C"; "jo.bloggs" → "JB".
export function initials(name) {
  const s = String(name || '').trim().replace(/@.*$/, '');
  const parts = s.split(/[\s._-]+/).filter(Boolean);
  if (!parts.length) return '?';
  const first = parts[0][0];
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase();
}

// A round badge: the logo when there is one and it loads, else initials.
export function ChatAvatar({ logoUrl, name, size = 22, title = null }) {
  const [failed, setFailed] = useState(false);
  const showLogo = !!logoUrl && !failed;
  return (
    <span aria-hidden="true" title={title != null ? title : (name || '')} style={{
      width: size, height: size, flexShrink: 0, borderRadius: '50%',
      border: `1px solid ${theme.border}`, overflow: 'hidden',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      background: showLogo ? theme.surface : 'var(--brand-50)',
      color: theme.brand, fontSize: Math.round(size * 0.4), fontWeight: 700,
      marginTop: 1,
    }}>
      {showLogo
        ? <img src={logoUrl} alt="" onError={() => setFailed(true)} style={{ width: '82%', height: '82%', objectFit: 'contain' }} />
        : initials(name)}
    </span>
  );
}

// v0.278.0 — Brett: "could the client see like a thinking symbol and brett is
// editing if they happen to go in the same item?" `people` = [{ name, typing }]
// from the live poll. The dots hold still for anyone who asks for less motion.
const PRESENCE_CSS = '@keyframes droppin-presence-dot { 0%, 80%, 100% { opacity: .25; transform: translateY(0); } 40% { opacity: 1; transform: translateY(-2px); } }'
  + ' @media (prefers-reduced-motion: reduce) { [data-presence-dot] { animation: none !important; opacity: .7 !important; } }';

function whoOf(list) {
  const names = list.map((p) => (p && p.name) || 'Someone from the office');
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names[0]} and ${names.length - 1} others`;
}

// v0.284.0 — `people` = [{ name, typing, who }]; who 'office' shows the logo
// when one is given, everyone else initials. Side by side, never stacked: at
// 20 px an overlap cuts the initials in half ("TC" read as "C"). Past `max`
// a "+N".
export function PresenceStrip({ people, logoUrl = null, max = 6 }) {
  const list = Array.isArray(people) ? people.filter(Boolean) : [];
  if (!list.length) return null;
  const shown = list.slice(0, max);
  const more = list.length - shown.length;
  const label = `On now: ${list.map((p) => (p.name || 'Someone from the office') + (p.typing ? ' (typing)' : '')).join(', ')}`;
  return (
    <span data-presence-strip={list.length} role="img" aria-label={label} title={label} style={{
      display: 'inline-flex', alignItems: 'center', gap: 3, flexShrink: 0,
    }}>
      <style>{PRESENCE_CSS}</style>
      {shown.map((p, i) => {
        const name = p.name || 'Someone from the office';
        return (
          <span key={`${name}-${i}`} data-presence-person={p.who || ''} style={{
            position: 'relative', display: 'inline-flex',
          }}>
            <ChatAvatar logoUrl={p.who === 'office' ? logoUrl : null} name={name} size={20}
              title={p.typing ? `${name} · typing` : name} />
            {p.typing && (
              <span data-presence-typing aria-hidden="true" style={{
                position: 'absolute', right: -1, bottom: -1, width: 7, height: 7, borderRadius: '50%',
                background: theme.success, boxShadow: `0 0 0 2px ${theme.surface}`,
                animation: 'droppin-presence-dot 1.2s infinite ease-in-out',
              }} data-presence-dot />
            )}
          </span>
        );
      })}
      {more > 0 && (
        <span style={{ marginLeft: 2, fontSize: 11, fontWeight: 600, color: theme.textLight }}>+{more}</span>
      )}
    </span>
  );
}

export function PresenceNote({ people }) {
  const list = Array.isArray(people) ? people.filter(Boolean) : [];
  const typing = list.filter((p) => p.typing);
  if (!typing.length) return null;
  const text = `${whoOf(typing)} ${typing.length === 1 ? 'is' : 'are'} typing a reply`;
  return (
    <>
    <style>{PRESENCE_CSS}</style>
    <div data-presence="typing" role="status" aria-live="polite" style={{
      display: 'flex', alignItems: 'center', gap: 6,
      fontSize: 11, fontWeight: 600, color: theme.textLight,
    }}>
      <span aria-hidden="true" style={{
        display: 'inline-flex', alignItems: 'center', gap: 3,
        padding: '5px 7px', borderRadius: 10,
        background: theme.surface, border: `1px solid ${theme.border}`,
      }}>
        {[0, 1, 2].map((i) => (
          <span key={i} data-presence-dot style={{
            width: 4, height: 4, borderRadius: '50%', background: theme.brand,
            animation: `droppin-presence-dot 1.2s ${i * 0.16}s infinite ease-in-out`,
          }} />
        ))}
      </span>
      {text}
    </div>
    </>
  );
}
